import { Check, ChevronDown, LoaderCircle, Search, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { cn } from '../lib/cn';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';

export type AutocompleteFilterOption = {
  id: string;
  label: string;
};

export interface AutocompleteFilterProps {
  label: string;
  options: AutocompleteFilterOption[];
  selectedIds: string[];
  onSelectedIdsChange: (nextIds: string[]) => void;
  selectAll?: boolean | undefined;
  icon?: ReactNode | undefined;
  disabled?: boolean | undefined;
  loading?: boolean | undefined;
  loadingPlaceholder?: string | undefined;
  className?: string | undefined;
  placeholder?: string | undefined;
  searchPlaceholder?: string | undefined;
  emptyMessage?: string | undefined;
  searchDebounceMs?: number | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  onSearchTermChange?: ((searchTerm: string) => void) | undefined;
}

export function AutocompleteFilter({
  label,
  options,
  selectedIds,
  onSelectedIdsChange,
  selectAll = true,
  icon,
  disabled = false,
  loading = false,
  loadingPlaceholder,
  className,
  placeholder = label,
  searchPlaceholder,
  emptyMessage = 'No results',
  searchDebounceMs = 0,
  onSearchTermChange,
  onOpenChange,
}: AutocompleteFilterProps) {
  const listboxId = useId();
  const controlRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');
  const lastEmittedSearch = useRef(search);
  const latestOnSearchTermChange = useRef(onSearchTermChange);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const visible = useMemo(
    () =>
      options.filter((option) => option.label.toLowerCase().includes(search.trim().toLowerCase())),
    [options, search],
  );

  function toggle(optionId: string) {
    const next = new Set(selected);
    if (next.has(optionId)) {
      next.delete(optionId);
    } else {
      next.add(optionId);
    }
    onSelectedIdsChange([...next]);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setPortalContainer(controlRef.current?.closest('[role="dialog"]') ?? null);
    }
    setOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }

  const hasSelection = selected.size > 0;
  const firstSelectedId = hasSelection ? selectedIds[0] : null;
  const firstLabel = firstSelectedId
    ? (options.find((option) => option.id === firstSelectedId)?.label ?? firstSelectedId)
    : null;
  const extraCount = selected.size - 1;
  const visibleIds = visible.map((option) => option.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((optionId) => selected.has(optionId));
  const someVisibleSelected = visibleIds.some((optionId) => selected.has(optionId));
  const triggerClassName = cn(
    'border-input bg-card text-foreground inline-flex h-9 w-full items-center gap-1.5 rounded-md border px-3 text-sm font-medium shadow-[var(--shadow-control)]',
    disabled && 'cursor-not-allowed opacity-50',
    className,
  );
  let listboxContent: ReactNode;

  if (loading) {
    listboxContent = (
      <div
        role="status"
        aria-label={`Loading ${label}`}
        className="text-text-tertiary flex items-center justify-center px-3 py-2"
      >
        <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
      </div>
    );
  } else if (visible.length === 0) {
    listboxContent = <p className="text-text-tertiary px-3 py-2 text-[12px]">{emptyMessage}</p>;
  } else {
    listboxContent = visible.map((option) => {
      const checked = selected.has(option.id);

      return (
        <button
          key={option.id}
          type="button"
          role="option"
          aria-selected={checked}
          onClick={() => {
            toggle(option.id);
          }}
          className="text-text-primary hover:bg-page flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-[12.5px] transition-colors"
        >
          <CheckboxIndicator checked={checked} />
          <span className="truncate">{option.label}</span>
        </button>
      );
    });
  }

  useEffect(() => {
    latestOnSearchTermChange.current = onSearchTermChange;
  }, [onSearchTermChange]);

  useEffect(() => {
    if (!latestOnSearchTermChange.current || search === lastEmittedSearch.current) return;
    // Clearing is applied at once so results of the previous term do not linger.
    const timeoutId = window.setTimeout(
      () => {
        lastEmittedSearch.current = search;
        latestOnSearchTermChange.current?.(search);
      },
      search.trim() ? searchDebounceMs : 0,
    );
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [search, searchDebounceMs]);

  return (
    <Popover open={disabled ? false : open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div ref={controlRef} className={triggerClassName}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={label}
              aria-controls={listboxId}
              aria-expanded={open}
              aria-haspopup="listbox"
              disabled={disabled}
              className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left disabled:cursor-not-allowed"
            >
              {icon ? (
                <span className="shrink-0" aria-hidden="true">
                  {icon}
                </span>
              ) : null}
              {hasSelection ? (
                <>
                  <span className="min-w-0 flex-1 truncate text-left">{firstLabel}</span>
                  {extraCount > 0 ? (
                    <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-1.5 py-px text-[10px] leading-none font-bold">
                      +{extraCount}
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  <span className="min-w-0 truncate">
                    {loading ? (loadingPlaceholder ?? `Loading ${label}...`) : placeholder}
                  </span>
                  <ChevronDown
                    className="text-muted-foreground ml-auto h-4 w-4 shrink-0"
                    aria-hidden="true"
                  />
                </>
              )}
            </button>
          </PopoverTrigger>
          {hasSelection ? (
            <button
              type="button"
              aria-label={`Clear ${label}`}
              disabled={disabled}
              className="ml-0.5 shrink-0 cursor-pointer opacity-70 transition-opacity hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={(event) => {
                event.stopPropagation();
                onSelectedIdsChange([]);
              }}
            >
              <X className="h-2.5 w-2.5" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        portalContainer={portalContainer}
        aria-label={`${label} options`}
        className="border-border-default w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-lg p-0 shadow-lg"
      >
        <div className="border-border-default border-b px-2.5 pt-2.5 pb-1.5">
          <div className="relative">
            <Search
              className="text-text-tertiary pointer-events-none absolute top-1/2 left-2.5 h-3 w-3 -translate-y-1/2"
              aria-hidden="true"
            />
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
              placeholder={searchPlaceholder ?? `Search ${label.toLowerCase()}...`}
              className="border-border-default text-text-primary placeholder:text-text-tertiary focus:ring-ring bg-page h-7 w-full rounded-md border pr-2 pl-7 text-[12px] focus:ring-1 focus:outline-none"
            />
          </div>
        </div>

        {!loading && selectAll && visible.length > 0 ? (
          <div className="border-border-default border-b py-1">
            <button
              type="button"
              onClick={() => {
                if (allVisibleSelected) {
                  const next = new Set(selected);
                  visibleIds.forEach((optionId) => next.delete(optionId));
                  onSelectedIdsChange([...next]);
                } else {
                  onSelectedIdsChange([...new Set([...selectedIds, ...visibleIds])]);
                }
              }}
              className="text-text-primary hover:bg-page flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-[12.5px] transition-colors"
            >
              <CheckboxIndicator checked={allVisibleSelected} indeterminate={someVisibleSelected} />
              <span className="font-medium">Select all</span>
            </button>
          </div>
        ) : null}

        <div
          id={listboxId}
          role="listbox"
          aria-label={label}
          aria-multiselectable="true"
          className="max-h-52 overflow-y-auto py-1"
        >
          {listboxContent}
        </div>

        {hasSelection ? (
          <div className="border-border-default flex items-center justify-between border-t px-3 py-2">
            <span className="text-text-tertiary text-[11px]">{selected.size} selected</span>
            <button
              type="button"
              onClick={() => {
                onSelectedIdsChange([]);
              }}
              className="text-text-secondary hover:text-text-primary cursor-pointer text-[11.5px] transition-colors"
            >
              Clear all
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

function CheckboxIndicator({
  checked,
  indeterminate = false,
}: {
  checked: boolean;
  indeterminate?: boolean | undefined;
}) {
  const className = cn(
    'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
    resolveCheckboxIndicatorClassName({ checked, indeterminate }),
  );

  return (
    <div className={className}>
      {checked ? (
        <Check className="text-primary-foreground h-2.5 w-2.5" aria-hidden="true" />
      ) : null}
      {!checked && indeterminate ? <div className="bg-primary h-1.5 w-1.5 rounded-sm" /> : null}
    </div>
  );
}

function resolveCheckboxIndicatorClassName({
  checked,
  indeterminate,
}: {
  checked: boolean;
  indeterminate: boolean;
}) {
  if (checked) return 'border-primary bg-primary';
  if (indeterminate) return 'border-primary bg-accent';
  return 'border-border-default bg-card';
}
